import { html } from 'lit-element';
import { cache } from 'lit-html/directives/cache';
import { ifDefined } from 'lit-html/directives/if-defined';
import { nothing } from 'lit-html';
import {
  KatLitElement,
  KatalComponent,
  Keys,
  booleanConverter,
  coerceToArray,
} from '../../shared/base';
import { fitHandler } from './fit-handler';

/**
 * @component {kat-dropdown} KatalDropdown A dropdown is a form element that allows the selection of a single choice from a list of items.
 * @guideline Do Stringify data object when passing it to attribute data
 * @guideline Do If possible, pre-select a good default item from the list to help the customer make an informed decision——similar to having a good default input value in an input field.
 * @guideline Do If possible, only include valid options in a dropdown. A user should typically not be able to choose an option that is invalid.
 * @guideline Dont Do not use the descriptions under the labels for each item unless absolutely necessary.
 * @example Basic {"value":"testValue2", "options": [{"name":"Value1","value":"testValue","icon":"optional-icon-name"},{"name":"Value2","value":"testValue2","icon":"optional-icon-name-2"}]}
 * @example Large {"value":"", "options": [{"name":"car","value":"carValue"},{"name":"truck","value":"truckValue"},{"name":"house","value":"houseValue"},{"name":"plane","value":"planeValue"},{"name":"cat","value":"catValue"},{"name":"dog","value":"dogValue"},{"name":"fish","value":"fishValue"},{"name":"bird","value":"birdValue"},{"name":"lizard","value":"lizardValue"},{"name":"turtle","value":"turtleValue"},{"name":"bug","value":"bugValue"}]}
 * @example Countries {"value": "", "label": "Country", "options": [{"name":"Afghanistan","value":"AF"},{"name":"Åland Islands","value":"AX"},{"name":"Albania","value":"AL"},{"name":"Algeria","value":"DZ"},{"name":"American Samoa","value":"AS"},{"name":"AndorrA","value":"AD"},{"name":"Angola","value":"AO"},{"name":"Anguilla","value":"AI"},{"name":"Antarctica","value":"AQ"},{"name":"Antigua and Barbuda","value":"AG"},{"name":"Argentina","value":"AR"},{"name":"Armenia","value":"AM"},{"name":"Aruba","value":"AW"},{"name":"Australia","value":"AU"},{"name":"Austria","value":"AT"},{"name":"Azerbaijan","value":"AZ"},{"name":"Bahamas","value":"BS"},{"name":"Bahrain","value":"BH"},{"name":"Bangladesh","value":"BD"},{"name":"Barbados","value":"BB"},{"name":"Belarus","value":"BY"},{"name":"Belgium","value":"BE"},{"name":"Belize","value":"BZ"},{"name":"Benin","value":"BJ"},{"name":"Bermuda","value":"BM"},{"name":"Bhutan","value":"BT"},{"name":"Bolivia","value":"BO"},{"name":"Bosnia and Herzegovina","value":"BA"},{"name":"Botswana","value":"BW"},{"name":"Bouvet Island","value":"BV"},{"name":"Brazil","value":"BR"},{"name":"British Indian Ocean Territory","value":"IO"},{"name":"Brunei Darussalam","value":"BN"},{"name":"Bulgaria","value":"BG"},{"name":"Burkina Faso","value":"BF"},{"name":"Burundi","value":"BI"},{"name":"Cambodia","value":"KH"},{"name":"Cameroon","value":"CM"},{"name":"Canada","value":"CA"},{"name":"Cape Verde","value":"CV"},{"name":"Cayman Islands","value":"KY"},{"name":"Central African Republic","value":"CF"},{"name":"Chad","value":"TD"},{"name":"Chile","value":"CL"},{"name":"China","value":"CN"},{"name":"Christmas Island","value":"CX"},{"name":"Cocos (Keeling) Islands","value":"CC"},{"name":"Colombia","value":"CO"},{"name":"Comoros","value":"KM"},{"name":"Congo","value":"CG"},{"name":"Congo, The Democratic Republic of the","value":"CD"},{"name":"Cook Islands","value":"CK"},{"name":"Costa Rica","value":"CR"},{"name":"Côte d’Ivoire","value":"CI"},{"name":"Croatia","value":"HR"},{"name":"Cuba","value":"CU"},{"name":"Cyprus","value":"CY"},{"name":"Czech Republic","value":"CZ"},{"name":"Denmark","value":"DK"},{"name":"Djibouti","value":"DJ"},{"name":"Dominica","value":"DM"},{"name":"Dominican Republic","value":"DO"},{"name":"Ecuador","value":"EC"},{"name":"Egypt","value":"EG"},{"name":"El Salvador","value":"SV"},{"name":"Equatorial Guinea","value":"GQ"},{"name":"Eritrea","value":"ER"},{"name":"Estonia","value":"EE"},{"name":"Ethiopia","value":"ET"},{"name":"Falkland Islands (Malvinas)","value":"FK"},{"name":"Faroe Islands","value":"FO"},{"name":"Fiji","value":"FJ"},{"name":"Finland","value":"FI"},{"name":"France","value":"FR"},{"name":"French Guiana","value":"GF"},{"name":"French Polynesia","value":"PF"},{"name":"French Southern Territories","value":"TF"},{"name":"Gabon","value":"GA"},{"name":"Gambia","value":"GM"},{"name":"Georgia","value":"GE"},{"name":"Germany","value":"DE"},{"name":"Ghana","value":"GH"},{"name":"Gibraltar","value":"GI"},{"name":"Greece","value":"GR"},{"name":"Greenland","value":"GL"},{"name":"Grenada","value":"GD"},{"name":"Guadeloupe","value":"GP"},{"name":"Guam","value":"GU"},{"name":"Guatemala","value":"GT"},{"name":"Guernsey","value":"GG"},{"name":"Guinea","value":"GN"},{"name":"Guinea-Bissau","value":"GW"},{"name":"Guyana","value":"GY"},{"name":"Haiti","value":"HT"},{"name":"Heard Island and Mcdonald Islands","value":"HM"},{"name":"Holy See (Vatican City State)","value":"VA"},{"name":"Honduras","value":"HN"},{"name":"Hong Kong","value":"HK"},{"name":"Hungary","value":"HU"},{"name":"Iceland","value":"IS"},{"name":"India","value":"IN"},{"name":"Indonesia","value":"ID"},{"name":"Iran, Islamic Republic Of","value":"IR"},{"name":"Iraq","value":"IQ"},{"name":"Ireland","value":"IE"},{"name":"Isle of Man","value":"IM"},{"name":"Israel","value":"IL"},{"name":"Italy","value":"IT"},{"name":"Jamaica","value":"JM"},{"name":"Japan","value":"JP"},{"name":"Jersey","value":"JE"},{"name":"Jordan","value":"JO"},{"name":"Kazakhstan","value":"KZ"},{"name":"Kenya","value":"KE"},{"name":"Kiribati","value":"KI"},{"name":"Korea, Democratic People's Republic of","value":"KP"},{"name":"Korea, Republic of","value":"KR"},{"name":"Kuwait","value":"KW"},{"name":"Kyrgyzstan","value":"KG"},{"name":"Lao People's Democratic Republic","value":"LA"},{"name":"Latvia","value":"LV"},{"name":"Lebanon","value":"LB"},{"name":"Lesotho","value":"LS"},{"name":"Liberia","value":"LR"},{"name":"Libyan Arab Jamahiriya","value":"LY"},{"name":"Liechtenstein","value":"LI"},{"name":"Lithuania","value":"LT"},{"name":"Luxembourg","value":"LU"},{"name":"Macao","value":"MO"},{"name":"Macedonia, The Former Yugoslav Republic of","value":"MK"},{"name":"Madagascar","value":"MG"},{"name":"Malawi","value":"MW"},{"name":"Malaysia","value":"MY"},{"name":"Maldives","value":"MV"},{"name":"Mali","value":"ML"},{"name":"Malta","value":"MT"},{"name":"Marshall Islands","value":"MH"},{"name":"Martinique","value":"MQ"},{"name":"Mauritania","value":"MR"},{"name":"Mauritius","value":"MU"},{"name":"Mayotte","value":"YT"},{"name":"Mexico","value":"MX"},{"name":"Micronesia, Federated States of","value":"FM"},{"name":"Moldova, Republic of","value":"MD"},{"name":"Monaco","value":"MC"},{"name":"Mongolia","value":"MN"},{"name":"Montserrat","value":"MS"},{"name":"Morocco","value":"MA"},{"name":"Mozambique","value":"MZ"},{"name":"Myanmar","value":"MM"},{"name":"Namibia","value":"NA"},{"name":"Nauru","value":"NR"},{"name":"Nepal","value":"NP"},{"name":"Netherlands","value":"NL"},{"name":"Netherlands Antilles","value":"AN"},{"name":"New Caledonia","value":"NC"},{"name":"New Zealand","value":"NZ"},{"name":"Nicaragua","value":"NI"},{"name":"Niger","value":"NE"},{"name":"Nigeria","value":"NG"},{"name":"Niue","value":"NU"},{"name":"Norfolk Island","value":"NF"},{"name":"Northern Mariana Islands","value":"MP"},{"name":"Norway","value":"NO"},{"name":"Oman","value":"OM"},{"name":"Pakistan","value":"PK"},{"name":"Palau","value":"PW"},{"name":"Palestinian Territory, Occupied","value":"PS"},{"name":"Panama","value":"PA"},{"name":"Papua New Guinea","value":"PG"},{"name":"Paraguay","value":"PY"},{"name":"Peru","value":"PE"},{"name":"Philippines","value":"PH"},{"name":"Pitcairn","value":"PN"},{"name":"Poland","value":"PL"},{"name":"Portugal","value":"PT"},{"name":"Puerto Rico","value":"PR"},{"name":"Qatar","value":"QA"},{"name":"Reunion","value":"RE"},{"name":"Romania","value":"RO"},{"name":"Russian Federation","value":"RU"},{"name":"RWANDA","value":"RW"},{"name":"Saint Helena","value":"SH"},{"name":"Saint Kitts and Nevis","value":"KN"},{"name":"Saint Lucia","value":"LC"},{"name":"Saint Pierre and Miquelon","value":"PM"},{"name":"Saint Vincent and the Grenadines","value":"VC"},{"name":"Samoa","value":"WS"},{"name":"San Marino","value":"SM"},{"name":"Sao Tome and Principe","value":"ST"},{"name":"Saudi Arabia","value":"SA"},{"name":"Senegal","value":"SN"},{"name":"Serbia and Montenegro","value":"CS"},{"name":"Seychelles","value":"SC"},{"name":"Sierra Leone","value":"SL"},{"name":"Singapore","value":"SG"},{"name":"Slovakia","value":"SK"},{"name":"Slovenia","value":"SI"},{"name":"Solomon Islands","value":"SB"},{"name":"Somalia","value":"SO"},{"name":"South Africa","value":"ZA"},{"name":"South Georgia and the South Sandwich Islands","value":"GS"},{"name":"Spain","value":"ES"},{"name":"Sri Lanka","value":"LK"},{"name":"Sudan","value":"SD"},{"name":"Suriname","value":"SR"},{"name":"Svalbard and Jan Mayen","value":"SJ"},{"name":"Swaziland","value":"SZ"},{"name":"Sweden","value":"SE"},{"name":"Switzerland","value":"CH"},{"name":"Syrian Arab Republic","value":"SY"},{"name":"Taiwan, Province of China","value":"TW"},{"name":"Tajikistan","value":"TJ"},{"name":"Tanzania, United Republic of","value":"TZ"},{"name":"Thailand","value":"TH"},{"name":"Timor-Leste","value":"TL"},{"name":"Togo","value":"TG"},{"name":"Tokelau","value":"TK"},{"name":"Tonga","value":"TO"},{"name":"Trinidad and Tobago","value":"TT"},{"name":"Tunisia","value":"TN"},{"name":"Turkey","value":"TR"},{"name":"Turkmenistan","value":"TM"},{"name":"Turks and Caicos Islands","value":"TC"},{"name":"Tuvalu","value":"TV"},{"name":"Uganda","value":"UG"},{"name":"Ukraine","value":"UA"},{"name":"United Arab Emirates","value":"AE"},{"name":"United Kingdom","value":"GB"},{"name":"United States","value":"US"},{"name":"United States Minor Outlying Islands","value":"UM"},{"name":"Uruguay","value":"UY"},{"name":"Uzbekistan","value":"UZ"},{"name":"Vanuatu","value":"VU"},{"name":"Venezuela","value":"VE"},{"name":"Viet Nam","value":"VN"},{"name":"Virgin Islands, British","value":"VG"},{"name":"Virgin Islands, U.S.","value":"VI"},{"name":"Wallis and Futuna","value":"WF"},{"name":"Western Sahara","value":"EH"},{"name":"Yemen","value":"YE"},{"name":"Zambia","value":"ZM"},{"name":"Zimbabwe","value":"ZW"}] }
 * @example Error {"value": "", "label": "Toppings", "placeholder": "Pick a topping", "state": "error", "state-emphasis": "Required", "state-label": "A topping is required", "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @example WithTooltip {"label": "With Tooltip", "tooltip-text": "A tooltip with some additional information", "tooltip-position": "right", "options": [{"name":"Value1","value":"testValue","icon":"optional-icon-name"},{"name":"Value2","value":"testValue2","icon":"optional-icon-name-2"}]}
 * @event {{ value: string }} change This event is fired whenever the dropdown is finished updating
 * @event {{ expanded: boolean }} expanded This event is fired whenever the dropdown is expanded/collapsed
 * @status Production
 * @theme flo
 */
export class KatalDropdown extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {json} options Array of `Option` objects, e.g. [{"name":"test","value":"testValue","icon":"optional-icon-name"}]
       * @jsonExample - {"options": "[{"name":"test","value":"testValue","icon":"optional-icon-name"}]"}
       * @required
       */
      options: { type: Array },

      /**
       * @classprop {string} placeholder The text that will be displayed to the user as a placeholder.
       */
      placeholder: { type: String, reflect: true },

      /**
       * @classprop {string} max-height The max height of the selection area.
       * This should be a css value with units, e.g. "8px".
       */
      maxHeight: { type: String, attribute: 'max-height', reflect: true },

      /**
       * @classprop {string} label The optional field label.
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} name The optional field name, hidden from view.
       */
      name: { type: String, reflect: true },

      /**
       * @classprop {boolean} expanded Whether the component is currently showing its dropdown options.
       */
      expanded: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {string} value The value of the selected option.
       */
      value: { type: String, reflect: true },

      /**
       * @classprop {enum} size The size of the selected option.
       * @enum {value} large Large select box - Default
       * @enum {value} small Small select box
       */
      size: { type: String, reflect: true },

      /**
       * @classprop {boolean} disabled Setting the select box to disabled will stop its interactivity.
       */
      disabled: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {string} unique-id Used to associate an external label to an input.  See the kat-label for attribute.
       */
      uniqueId: { type: String, attribute: 'unique-id', reflect: true },

      /**
       * @classprop {string} constraint-emphasis Optional pretext for the constraint label.  Used to provide addtional context to the constraint for the input
       */
      constraintEmphasis: {
        type: String,
        attribute: 'constraint-emphasis',
        reflect: true,
      },

      /**
       * @classprop {string} constraint-label Provides users with more information about what they enter into the input
       */
      constraintLabel: {
        type: String,
        attribute: 'constraint-label',
        reflect: true,
      },

      /**
       * @classprop {enum} state Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
       * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
       */
      state: { type: String, reflect: true },

      /**
       * @param {string} state-emphasis Optional pretext for the state label. Provides additional context to the state for the input.
       */
      stateEmphasis: {
        type: String,
        attribute: 'state-emphasis',
        reflect: true,
      },

      /**
       * @param {string} state-label Provides users with more information about why the input is in the state it is in
       */
      stateLabel: { type: String, attribute: 'state-label', reflect: true },

      /**
       * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        converter: booleanConverter,
        attribute: 'katal-1-2-spacing-fixes',
        reflect: true,
      },

      /**
       * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
       * set.
       */
      tooltipText: { type: String, attribute: 'tooltip-text', reflect: true },

      /**
       * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
       */
      tooltipPosition: {
        type: String,
        attribute: 'tooltip-position',
        reflect: true,
      },

      /**
       * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
       */
      tooltipTriggerIcon: {
        type: String,
        attribute: 'tooltip-trigger-icon',
        reflect: true,
      },
    };
  }

  constructor() {
    super();

    this._uniqueId = KatalComponent.generateRandomId();
    this._jumpToSearch = '';
    this._timeoutId = 0;

    this._options = [];
    this.size = 'large';
    this.uniqueId = this._uniqueId;
    this.tooltipPosition = 'top';
    this.tooltipTriggerIcon = 'help_outline';
  }

  get options() {
    return this._options;
  }

  /**
   * @deprecated
   * This logic will be removed in 1.3
   */
  set options(val) {
    const oldVal = this.options;
    this._options = coerceToArray(this, 'options', val);
    this.requestUpdate('options', oldVal);
  }

  firstUpdated() {
    this.createEvents();
  }

  updated(changedProperties) {
    if (changedProperties.has('expanded')) {
      this.setOrUnsetOptionsContainerBottomOffset();
    }
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  renderOptions() {
    return html`
      ${cache(
        this.expanded
          ? this.options.map((option, index) => {
              const { name, value, icon } = option;
              const selected = value === this.value ? 'selected' : '';

              const iconElement = icon
                ? html`
                    <kat-icon
                      class="optional-icon"
                      name="${icon}"
                      size="tiny"
                    ></kat-icon>
                  `
                : nothing;

              return html`
                <div
                  class="${'option-row ' + selected}"
                  id="${value + index}"
                  tabindex="-1"
                  data-value="${value}"
                  data-name="${name}"
                >
                  <div class="option">
                    ${name}
                  </div>
                  <div class="optional-icon">
                    ${iconElement}
                  </div>
                </div>
              `;
            })
          : nothing
      )}
    `;
  }

  render() {
    const formLabel = this.label
      ? html`
          <kat-label
            class="form-label"
            for="${this.uniqueId}"
            text="${this.label}"
            tooltip-text="${ifDefined(this.tooltipText)}"
            tooltip-position="${this.tooltipPosition}"
            tooltip-trigger-icon="${this.tooltipTriggerIcon}"
          ></kat-label>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <kat-label
            class="constraint-label"
            for="${this.uniqueId}"
            variant="constraint"
            emphasis="${ifDefined(this.constraintEmphasis)}"
            text="${this.constraintLabel}"
          ></kat-label>
        `
      : nothing;

    const stateLabel = this.stateLabel
      ? html`
          <kat-label
            class="state-label"
            variant="constraint"
            text="${this.stateLabel}"
            emphasis="${ifDefined(this.stateEmphasis)}"
            state="${this.state}"
          ></kat-label>
        `
      : nothing;

    let displayTextClass = 'header-row-text ';
    const valueOrPlaceholder = this.value ? 'value ' : 'placeholder ';
    displayTextClass += valueOrPlaceholder;
    if (this.state) {
      displayTextClass += this.state;
    }

    const size = this.size === 'small' ? 'small' : 'large';
    const containerClass = 'kat-select-container ' + size;

    const disabled = this.disabled ? ' disabled' : '';

    const selectHeaderClass = 'select-header' + disabled;

    let displayText = '';
    if (this.value) {
      const setValue = this.value;
      const values = this.options.filter(object => {
        return object.value === setValue;
      });
      if (values.length > 0) {
        displayText = values[0].name;
      }
    }

    if (!displayText && this.placeholder) {
      displayText = this.placeholder;
    }

    return html`
      ${formLabel}
      <div class="${containerClass}">
        <div
          class="${selectHeaderClass}"
          tabindex="0"
          @click=${this.headerClick}
          @keydown=${this.headerKeydown}
        >
          <div class="header-row">
            <div class="${displayTextClass}">
              ${displayText}
            </div>
            <div class="indicator">
              <kat-icon name="chevron-down" size="small"></kat-icon>
            </div>
          </div>
        </div>
        <div
          class="select-options"
          @click=${this.selectOptionClick}
          @keydown=${this.selectOptionKeydown}
        >
          <div
            class="option-inner-container"
            style="${ifDefined(
              this.maxHeight &&
                'max-height:' + this.maxHeight + ';overflow:auto;'
            )}"
          >
            ${this.renderOptions()}
          </div>
        </div>
        <input
          type="hidden"
          name="${ifDefined(this.name)}"
          value="${ifDefined(this.value)}"
          id="${this.uniqueId}"
        />
      </div>
      ${constraintLabel} ${stateLabel}
    `;
  }

  createEvents() {
    this.addEventListener('focusout', e => {
      const container = this.querySelector('.kat-select-container');
      const newFocus = e.relatedTarget;
      if (!this.disabled && !container.contains(newFocus)) {
        this.expanded = false;
      }
    });
  }

  searchOptions() {
    const search = this._jumpToSearch.toLowerCase();
    const domOptions = Array.from(this.querySelectorAll('.option-row')).filter(
      e => e.dataset.name.toLowerCase().startsWith(search)
    );

    if (domOptions.length !== 0) {
      domOptions[0].focus();
    }
  }

  selectOption(element) {
    if (!element.classList.contains('option-row')) {
      return;
    }

    const initialValue = this.value;
    const newValue = element.dataset.value;
    if (initialValue !== newValue) {
      this.value = newValue;
      this.dispatchCustomEvent('change', { value: this.value });
    }
    this.expanded = false;
  }

  async toggleExpanded() {
    this.expanded = !this.expanded;
    await this.updateComplete;
    this.dispatchCustomEvent('expanded', { expanded: this.expanded }, false);
  }

  fitDropDownWindow() {
    const optionContainer = this.querySelector('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );

    optionContainer.style.display = 'table';
    fitHandler(this, optionInnerContainer);
    optionContainer.style.removeProperty('display');
  }

  setOrUnsetOptionsContainerBottomOffset() {
    const selectOptionsElement = this.querySelector('.select-options');

    if (!selectOptionsElement) {
      return;
    }

    this.fitDropDownWindow();

    if (this.hasAttribute('drop-up')) {
      const offset = this.querySelector('div.select-header').offsetHeight;

      selectOptionsElement.setAttribute('style', `bottom: ${offset}px`);
    } else {
      selectOptionsElement.removeAttribute('style');
    }
  }

  selectOptionClick(event) {
    if (this.disabled) {
      return;
    }

    let el = event.target;

    while (el && el !== this && !el.hasAttribute('data-value')) {
      el = el.parentElement;
    }

    if (el && el !== this) {
      this.selectOption(el);
    }
  }

  selectOptionKeydown(event) {
    const target = event.target;
    const keyCode = event.keyCode || event.which;

    if (!this.disabled && target) {
      if (
        (keyCode === Keys.Space && !this._jumpToSearch) ||
        keyCode === Keys.Enter
      ) {
        event.preventDefault();
        this.selectOption(target);
      } else if (keyCode === Keys.ArrowUp) {
        //previous
        event.preventDefault();
        const prevEl = target.previousElementSibling;
        if (prevEl) {
          prevEl.focus();
        } else {
          const katSelectContainer =
            target.parentElement.parentElement.parentElement;
          const input = katSelectContainer.children[0].children[0].children[0];
          input.focus();
        }
      } else if (keyCode === Keys.ArrowDown) {
        //next
        event.preventDefault();
        const nextEl = target.nextElementSibling;
        if (nextEl) {
          nextEl.focus();
        }
      } else {
        this.jumpToSearch(keyCode);
      }
    }
  }

  headerKeydown(event) {
    const keyCode = event.keyCode || event.which;
    const options = Array.from(
      event.target.nextElementSibling.firstElementChild.children
    );
    const optionsLength = options.length;

    if (
      (keyCode === Keys.ArrowDown || keyCode === Keys.Space) &&
      optionsLength > 0
    ) {
      event.preventDefault();
      const selectedOption = options.filter(element =>
        element.classList.contains('selected')
      );

      this.expanded = true;
      selectedOption.length > 0
        ? selectedOption[0].focus()
        : options[0].focus();
    } else {
      this.jumpToSearch(keyCode);
    }
  }

  headerClick(e) {
    const container = this.querySelector('.kat-select-container');
    const newFocus = e.relatedTarget;
    if (!this.disabled && !container.contains(newFocus)) {
      this.toggleExpanded();
    }
  }

  jumpToSearch(keyCode) {
    this._jumpToSearch += String.fromCharCode(keyCode);

    this.searchOptions();
    clearTimeout(this._timeoutId);
    this._timeoutId = setTimeout(() => (this._jumpToSearch = ''), 300);
  }
}

KatalComponent.register('kat-dropdown', KatalDropdown);
